import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";

const UPLOAD_TIMEOUT_MS = 300_000;

// CAD 3D upload + async analysis (svc-09 via gateway /v1/cad/...).
// See docs/spec/cad3d-settings-and-analyzer.md. Customers (external) only ever see
// upload + status + GLB preview — similarity matches are internal-only.

export type CadAnalysisStatus = "pending" | "running" | "ready" | "failed";

export interface CadUploadResult {
  applicationId: string;
  fileId: string;
  fileName: string;
  sha256: string;
  status: CadAnalysisStatus;
}

export interface CadStatus {
  applicationId: string;
  status: CadAnalysisStatus | null;
  engineVersion: string | null;
  fileName: string | null;
  error: string | null;
  hasPreview: boolean;
}

export interface CadGlbUrl {
  url: string;
}

export interface CadBestMatch {
  fileId: string | null;
  fileName: string | null;
  applicationId: string | null;
}

// Geometry properties for the stepcheck properties panel (POC parity).
export interface CadDetails {
  applicationId: string;
  fileId: string | null;
  fileName: string | null;
  sha256: string | null;
  uploadedAt: string | null;
  status: CadAnalysisStatus | null;
  engineVersion: string | null;
  hasPreview: boolean;
  signatureDiagonalMm: number | null;
  /** Exact solid volume (mm³) from svc-09 OCP GProp; preferred for weight. */
  volumeMm3: number | null;
  meshes: number | null;
  vertices: number | null;
  tessellation: {
    linearUnit: string | null;
    linearDeflection: number | null;
    angularDeflection: number | null;
  };
  // internal-only
  bestScore?: number | null;
  bestMatch?: CadBestMatch | null;
}

export interface CadSimilarCandidate {
  applicationId: string;
  fileId: string | null;
  fileName: string | null;
  score: number;
  isMatch: boolean;
}

export interface CadSearchResult {
  applicationId: string;
  engineVersion: string;
  matchThreshold: number;
  candidates: CadSimilarCandidate[];
}

export async function uploadCadStep(
  applicationId: string,
  file: File,
): Promise<CadUploadResult> {
  const form = new FormData();
  // Field names match svc-09 multipart form params (application_id, file).
  form.append("application_id", applicationId);
  form.append("file", file);
  const res = await Api.post("/v1/cad/uploads", form, {
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return unwrap<CadUploadResult>(res.data);
}

export async function getCadStatus(applicationId: string): Promise<CadStatus> {
  const res = await Api.get(
    `/v1/cad/uploads/${encodeURIComponent(applicationId)}/status`,
  );
  return unwrap<CadStatus>(res.data);
}

export async function getCadGlbUrl(applicationId: string): Promise<CadGlbUrl> {
  const res = await Api.get(
    `/v1/cad/uploads/${encodeURIComponent(applicationId)}/glb`,
  );
  return unwrap<CadGlbUrl>(res.data);
}

export async function getCadDetails(applicationId: string): Promise<CadDetails> {
  const res = await Api.get(
    `/v1/cad/uploads/${encodeURIComponent(applicationId)}/details`,
  );
  return unwrap<CadDetails>(res.data);
}

// Delete the current 3D STEP upload — removes the STEP + GLB from S3 and the
// cad3d library rows server-side (svc-09).
export async function deleteCadStep(applicationId: string): Promise<void> {
  await Api.delete(`/v1/cad/uploads/${encodeURIComponent(applicationId)}`);
}

// Internal-only (svc-09 requires x-auth-scope: internal). The axios interceptor
// sets that header for internal users; external users get 403 here.
export async function searchSimilarParts(
  applicationId: string,
  limit = 10,
): Promise<CadSearchResult> {
  const res = await Api.post("/v1/cad/signatures/search", {
    applicationId,
    limit,
  });
  return unwrap<CadSearchResult>(res.data);
}
